import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search,
  MapPin,
  Filter,
  X,
  ShoppingBag,
  Waves,
  Camera,
  Utensils,
  Coffee,
  TreePine,
  Building2,
  Store,
  Car,
  Wine,
  Music,
  Bus,
  Bike,
  SlidersHorizontal,
  Navigation,
  Clock,
  List,
  ChevronLeft,
} from 'lucide-react';
import { LocationCategory, SearchSuggestion, Location } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { calculateDistance, formatDistance } from '../utils/distance';
import { createPortal } from 'react-dom';
import {
  BRANDS,
  BRAND_ICONS,
  LOCATION_ONLY_BRANDS,
  SUPERMARKET_BRANDS,
  ELECTRONICS_BRANDS,
  type Brand,
  normalizeBrandName,
  matchesBrand,
} from '../utils/brands';

interface SearchDropdownProps {
  options: string[];
  onSelect: (locationName: string) => void;
  onCategoryFilter: (category: LocationCategory) => void;
  selectedCategory: LocationCategory | null;
  onParkAndRideSelect: (locationName: string) => void;
  onBicycleParkingSelect: (locationName: string) => void;
  onMobilityParkingSelect: (locationName: string) => void;
  onCarShareBayOperatorSelect: (locationName: string) => void;
  allLocations?: Record<string, Location>;
  userLocation?: [number, number] | null; // [latitude, longitude]
  onOverlayStateChange?: (isActive: boolean) => void;
  /** Render in a portal overlay (for modals) - ensures consistent positioning */
  usePortal?: boolean;
  /** Custom container class name for styling */
  containerClassName?: string;
  /** Callback when search is closed */
  onClose?: () => void;
}

interface SearchResult {
  text: string;
  type: 'location' | 'category' | 'park-and-ride' | 'bicycle-parking' | 'mobility-parking' | 'car-share-bay-operator';
  distance?: number;
  category?: LocationCategory;
  locationData?: Location;
  matchScore?: number;
}

/**
 * Calculate fuzzy match score (0-1) for search relevance
 */
const calculateMatchScore = (text: string, searchQuery: string): number => {
  const lowerText = text.toLowerCase();
  const lowerSearch = searchQuery.toLowerCase().trim();

  if (lowerText === lowerSearch) return 1.0;
  if (lowerText.startsWith(lowerSearch)) return 0.9;
  if (lowerText.includes(lowerSearch)) return 0.7;

  // Word-based matching
  const searchWords = lowerSearch.split(/\s+/).filter((w) => w.length > 0);
  const textWords = lowerText.split(/\s+/);

  let matchedWords = 0;
  for (const word of searchWords) {
    if (textWords.some((tw) => tw.includes(word) || word.includes(tw))) {
      matchedWords++;
    }
  }

  if (matchedWords === searchWords.length) return 0.6;
  if (matchedWords > 0) return 0.3 + (matchedWords / searchWords.length) * 0.3;

  return 0;
};

/**
 * Highlight search terms in text
 */
const highlightText = (text: string, searchQuery: string): React.ReactNode => {
  if (!searchQuery || searchQuery.length < 2) return text;

  const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === searchQuery.toLowerCase() ? (
          <mark key={index} className="search-highlight">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

/**
 * Elite search algorithm: Unified, efficient search with smart prioritization
 * Brand filter simply pre-populates the search term for faster results
 * Optimized with locationMap for O(1) lookups
 */
const enhancedSearchHandler = (
  search: string,
  options: string[],
  allLocations?: Record<string, Location>,
  userLocation?: [number, number] | null,
  locationMap?: Map<string, Location>
): SearchResult[] => {
  const searchLower = search.toLowerCase().trim();

  // Minimum search length
  if (search.length < 2) {
    return [];
  }

  const results: SearchResult[] = [];
  const locationResults: SearchResult[] = [];
  const shopResults: SearchResult[] = [];
  const seenResults = new Set<string>(); // Fast O(1) duplicate checking

  // Check for brand category match (for UI display)
  const brandMatch = BRANDS.find(
    (brand) => brand.toLowerCase() === searchLower || brand.toLowerCase().includes(searchLower)
  );

  // Brands that should only return locations (not shops)
  const isLocationOnlyBrand = brandMatch && LOCATION_ONLY_BRANDS.includes(brandMatch as Brand);

  if (brandMatch) {
    results.push({
      text: brandMatch,
      type: 'category',
      category: brandMatch as LocationCategory,
      matchScore: brandMatch.toLowerCase() === searchLower ? 1.0 : 0.8,
    });
  }

  // Single-pass unified search through options
  // Process all options efficiently in one loop
  const MAX_RESULTS = 50; // Reasonable limit for performance
  const searchWords = searchLower.split(/\s+/).filter((w) => w.length > 0);

  for (const option of options) {
    // Early exit if we have enough results
    if (locationResults.length + shopResults.length >= MAX_RESULTS) break;

    if (!option || typeof option !== 'string') continue;

    // Parse option (shop or location)
    const isShop = option.includes(':::');
    let locationName = '';
    let shopName = '';

    if (isShop) {
      const parts = option.split(' ::: ');
      locationName = parts[0] || '';
      shopName = parts[1] || '';
    } else {
      locationName = option;
    }

    // For location-only brands (like Westfield), skip shops entirely
    if (isLocationOnlyBrand && isShop) {
      continue;
    }

    // Fast text matching - check if search appears in location or shop name
    const locationLower = locationName.toLowerCase();
    const shopLower = shopName.toLowerCase();

    // Quick inclusion check (fastest path)
    const locationMatches = locationLower.includes(searchLower);
    const shopMatches = isShop && shopName && shopLower.includes(searchLower);

    // Word-based matching for multi-word searches (fallback)
    let wordMatch = false;
    if (!locationMatches && !shopMatches && searchWords.length > 1) {
      const locationWords = locationLower.split(/\s+/);
      const shopWords = shopLower.split(/\s+/);

      // Check if all search words appear in location or shop
      wordMatch = searchWords.every(
        (sw) => locationWords.some((lw) => lw.includes(sw)) || (isShop && shopWords.some((sw2) => sw2.includes(sw)))
      );

      if (!wordMatch) continue; // Skip early if no match
    }

    // Calculate match scores (always calculate for accurate ranking)
    const locationScore = locationMatches || wordMatch ? calculateMatchScore(locationName, search) : 0;
    const shopScore = shopMatches || (isShop && wordMatch) ? calculateMatchScore(shopName, search) : 0;

    // For shops, prioritize shop name matches
    const finalMatchScore = isShop
      ? Math.max(locationScore, shopScore * 1.3, (locationScore + shopScore) / 2)
      : locationScore;

    // Threshold: be lenient for brand searches, stricter for specific queries
    const isBrandQuery = BRANDS.some((b) => searchLower.includes(b.toLowerCase()));
    const scoreThreshold = isBrandQuery ? 0.1 : 0.15;

    if (finalMatchScore <= scoreThreshold && !locationMatches && !shopMatches) {
      continue;
    }

    // Get location data and calculate distance (optimized with Map lookup)
    let locationData: Location | undefined;
    let distance: number | undefined;

    if (allLocations && userLocation) {
      // Use Map for O(1) lookup instead of direct property access
      if (isShop) {
        locationData = locationMap?.get(locationName.toLowerCase()) || (allLocations[locationName] as Location);
      } else {
        locationData = locationMap?.get(option.toLowerCase()) || (allLocations[option] as Location);
      }

      // Calculate distance only if we have location data and coordinates
      if (locationData?.latitude && locationData?.longitude) {
        distance = calculateDistance(userLocation[0], userLocation[1], locationData.latitude, locationData.longitude);
      }
    }

    // Determine result type
    let type: SearchResult['type'] = 'location';
    if (option.startsWith('🚲 ')) {
      type = 'bicycle-parking';
    } else if (option.startsWith('🦽 ')) {
      type = 'mobility-parking';
    } else if (option.startsWith('🚗 ')) {
      type = 'car-share-bay-operator';
    } else if (option.toLowerCase().includes('park and ride') || option.toLowerCase().includes('park & ride')) {
      type = 'park-and-ride';
    }

    // Fast duplicate check using Set
    const resultKey = `${type}:${option}`;
    if (seenResults.has(resultKey)) continue;
    seenResults.add(resultKey);

    const result: SearchResult = {
      text: option,
      type,
      distance,
      locationData,
      matchScore: finalMatchScore,
      category: locationData?.category,
    };

    // Separate locations and shops for smart prioritization
    if (isShop) {
      shopResults.push(result);
    } else {
      locationResults.push(result);
    }
  }

  // Smart prioritization: prioritize based on result counts
  // If we have many locations, show locations first; if many shops, show shops first
  const locationCount = locationResults.length;
  const shopCount = shopResults.length;

  // Sort each group by distance (if available) then by match score
  locationResults.sort((a, b) => {
    if (a.distance !== undefined && b.distance !== undefined) return a.distance - b.distance;
    if (a.distance !== undefined) return -1;
    if (b.distance !== undefined) return 1;
    return (b.matchScore || 0) - (a.matchScore || 0);
  });

  shopResults.sort((a, b) => {
    if (a.distance !== undefined && b.distance !== undefined) return a.distance - b.distance;
    if (a.distance !== undefined) return -1;
    if (b.distance !== undefined) return 1;
    return (b.matchScore || 0) - (a.matchScore || 0);
  });

  // Combine results with smart prioritization
  // For location-only brands (like Westfield), only show locations
  if (isLocationOnlyBrand) {
    results.push(...locationResults);
  } else if (locationCount >= shopCount && locationCount > 0) {
    // More locations: show locations first, then shops
    results.push(...locationResults);
    results.push(...shopResults);
  } else if (shopCount > 0) {
    // More shops: show shops first, then locations
    results.push(...shopResults);
    results.push(...locationResults);
  } else {
    // Fallback: just add what we have
    results.push(...locationResults, ...shopResults);
  }

  // Limit final results
  const maxResults = 50;
  return results.slice(0, maxResults);
};

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  options,
  onSelect,
  onCategoryFilter,
  selectedCategory,
  onParkAndRideSelect,
  onBicycleParkingSelect,
  onMobilityParkingSelect,
  onCarShareBayOperatorSelect,
  allLocations,
  userLocation,
  onOverlayStateChange,
  usePortal = false,
  containerClassName = '',
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [viewMode, setViewMode] = useState<
    'recent' | 'brands' | 'around-me' | 'categories' | 'supermarket' | 'electronics' | 'beaches'
  >('recent'); // Default to recent searches
  // Removed sortBy state - always sort by distance
  const [isLocating, setIsLocating] = useState(false);
  const [currentUserLocation, setCurrentUserLocation] = useState<[number, number] | null>(null);
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoFocusedRef = useRef(false);
  const overlayStateNotifiedRef = useRef(false);
  const onOverlayStateChangeRef = useRef(onOverlayStateChange);

  // Keep ref updated with latest callback to avoid dependency issues
  useEffect(() => {
    onOverlayStateChangeRef.current = onOverlayStateChange;
  }, [onOverlayStateChange]);

  // Ensure notification happens on mount if usePortal is already true
  // This handles the case where SearchDropdown mounts with usePortal={true}
  useEffect(() => {
    if (usePortal && !overlayStateNotifiedRef.current) {
      overlayStateNotifiedRef.current = true;
      // Use the ref which is kept up to date
      onOverlayStateChangeRef.current?.(true);
      // Also call prop directly for immediate notification
      onOverlayStateChange?.(true);
    }
  }, []); // Only run on mount - usePortal check ensures it only runs when needed

  // Create location lookup map for O(1) access (memoized for performance)
  const locationMap = useMemo(() => {
    if (!allLocations) return new Map<string, Location>();
    const map = new Map<string, Location>();
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (locationData && typeof locationData === 'object') {
        const location = locationData as Location;
        const locationName = (location.location || locationId).toLowerCase();
        map.set(locationName, location);
        // Also index by locationId for direct lookups
        map.set(locationId.toLowerCase(), location);
      }
    }
    return map;
  }, [allLocations]);

  // Pre-compute brand lowercase for faster matching
  const supermarketBrandsLower = useMemo(() => SUPERMARKET_BRANDS.map((b) => b.toLowerCase()), []);
  const electronicsBrandsLower = useMemo(() => ELECTRONICS_BRANDS.map((b) => normalizeBrandName(b)), []);

  // Get supermarket results - 10 closest supermarkets overall (optimized)
  const getSupermarketResults = useMemo(() => {
    const activeUserLocation = currentUserLocation || userLocation;
    const supermarkets: SearchResult[] = [];

    // Early exit if no location data
    if (!allLocations) return [];

    // Search through locations (single pass)
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (!locationData || typeof locationData !== 'object') continue;

      const location = locationData as Location;
      const locationName = location.location || locationId;
      const locationNameLower = locationName.toLowerCase();

      // Fast brand matching using pre-computed lowercase brands
      const isSupermarket = supermarketBrandsLower.some((brandLower) => locationNameLower.includes(brandLower));

      if (isSupermarket && location.latitude && location.longitude) {
        const distance = activeUserLocation
          ? calculateDistance(activeUserLocation[0], activeUserLocation[1], location.latitude, location.longitude)
          : undefined;

        supermarkets.push({
          text: locationName,
          type: 'location',
          distance,
          locationData: location,
          matchScore: 1.0,
          category: location.category,
        });
      }
    }

    // Search through shops (optimized with Map lookup)
    for (const option of options) {
      if (!option || typeof option !== 'string') continue;

      const isShop = option.includes(':::');
      if (!isShop) continue;

      const parts = option.split(' ::: ');
      const locationName = parts[0] || '';
      const shopName = parts[1] || '';
      const shopNameLower = shopName.toLowerCase();

      // Fast brand matching
      const isSupermarket = supermarketBrandsLower.some((brandLower) => shopNameLower.includes(brandLower));

      if (isSupermarket) {
        // O(1) lookup instead of O(n) iteration
        const locationData = locationMap.get(locationName.toLowerCase());
        const distance =
          locationData && locationData.latitude && locationData.longitude && activeUserLocation
            ? calculateDistance(
                activeUserLocation[0],
                activeUserLocation[1],
                locationData.latitude,
                locationData.longitude
              )
            : undefined;

        supermarkets.push({
          text: `${locationName} ::: ${shopName}`,
          type: 'location',
          distance,
          locationData: locationData,
          matchScore: 1.0,
        });
      }
    }

    // Sort by distance if available, otherwise by name
    supermarkets.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    // Return top 10 closest
    return supermarkets.slice(0, 10);
  }, [allLocations, options, currentUserLocation, userLocation, locationMap, supermarketBrandsLower]);

  // Get electronics results - 10 closest electronics stores overall (optimized)
  const getElectronicsResults = useMemo(() => {
    const activeUserLocation = currentUserLocation || userLocation;
    const electronics: SearchResult[] = [];

    // Early exit if no location data
    if (!allLocations) return [];

    // Search through locations (single pass)
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (!locationData || typeof locationData !== 'object') continue;

      const location = locationData as Location;
      const locationName = location.location || locationId;
      const locationNameLower = locationName.toLowerCase();

      // Exclude "adams apple" from electronics stores
      if (locationNameLower.includes('adams apple')) {
        continue;
      }

      // Fast brand matching using pre-computed normalized brands
      const isElectronics = electronicsBrandsLower.some((brandNormalized) =>
        locationNameLower.includes(brandNormalized)
      );

      if (isElectronics && location.latitude && location.longitude) {
        const distance = activeUserLocation
          ? calculateDistance(activeUserLocation[0], activeUserLocation[1], location.latitude, location.longitude)
          : undefined;

        electronics.push({
          text: locationName,
          type: 'location',
          distance,
          locationData: location,
          matchScore: 1.0,
          category: location.category,
        });
      }
    }

    // Search through shops (optimized with Map lookup)
    for (const option of options) {
      if (!option || typeof option !== 'string') continue;

      const isShop = option.includes(':::');
      if (!isShop) continue;

      const parts = option.split(' ::: ');
      const locationName = parts[0] || '';
      const shopName = parts[1] || '';
      const shopNameLower = shopName.toLowerCase();

      // Exclude "adams apple" from electronics stores
      if (shopNameLower.includes('adams apple')) {
        continue;
      }

      // Fast brand matching
      const isElectronics = electronicsBrandsLower.some((brandNormalized) => shopNameLower.includes(brandNormalized));

      if (isElectronics) {
        // O(1) lookup instead of O(n) iteration
        const locationData = locationMap.get(locationName.toLowerCase());
        const distance =
          locationData && locationData.latitude && locationData.longitude && activeUserLocation
            ? calculateDistance(
                activeUserLocation[0],
                activeUserLocation[1],
                locationData.latitude,
                locationData.longitude
              )
            : undefined;

        electronics.push({
          text: `${locationName} ::: ${shopName}`,
          type: 'location',
          distance,
          locationData: locationData,
          matchScore: 1.0,
        });
      }
    }

    // Sort by distance if available, otherwise by name
    electronics.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    // Return top 10 closest
    return electronics.slice(0, 10);
  }, [allLocations, options, currentUserLocation, userLocation, locationMap, electronicsBrandsLower]);

  // Get beaches results - all beaches sorted by distance (optimized)
  const getBeachesResults = useMemo(() => {
    const activeUserLocation = currentUserLocation || userLocation;
    const beaches: SearchResult[] = [];

    // Early exit if no location data
    if (!allLocations) return [];

    // Search through locations (single pass)
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (!locationData || typeof locationData !== 'object') continue;

      const location = locationData as Location;
      const locationName = location.location || locationId;

      // Check if location is a beach
      const isBeach = location.category === 'Beaches';

      if (isBeach && location.latitude && location.longitude) {
        const distance = activeUserLocation
          ? calculateDistance(activeUserLocation[0], activeUserLocation[1], location.latitude, location.longitude)
          : undefined;

        beaches.push({
          text: locationName,
          type: 'location',
          distance,
          locationData: location,
          matchScore: 1.0,
          category: location.category,
        });
      }
    }

    // Sort by distance if available, otherwise by name
    beaches.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    // Return all beaches (no limit)
    return beaches;
  }, [allLocations, currentUserLocation, userLocation]);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('vibecheck_recent_searches');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentSearches(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        setRecentSearches([]);
      }
    }
  }, []);

  // Save search to recent searches
  const saveToRecentSearches = (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length === 0) return;

    const updated = [searchTerm, ...recentSearches.filter((s) => s !== searchTerm)].slice(0, 10); // Keep last 10
    setRecentSearches(updated);
    localStorage.setItem('vibecheck_recent_searches', JSON.stringify(updated));
  };

  // Memoized search results with distance ranking
  const searchResults = useMemo(() => {
    // Use currentUserLocation if available, otherwise fall back to userLocation prop
    const activeUserLocation = currentUserLocation || userLocation;

    // If current location is set and search is empty, show all nearby locations
    if (currentUserLocation && search.length === 0) {
      // Get all locations and sort by distance (optimized with early limit)
      const allLocationResults: SearchResult[] = [];
      const MAX_AROUND_ME_RESULTS = 50;

      // Add all locations from options (with early exit)
      if (allLocations) {
        for (const [locationId, locationData] of Object.entries(allLocations)) {
          // Early exit if we have enough results
          if (allLocationResults.length >= MAX_AROUND_ME_RESULTS) break;

          if (!locationData || typeof locationData !== 'object') continue;

          const location = locationData as Location;
          if (location.latitude && location.longitude) {
            const distance = calculateDistance(
              currentUserLocation[0],
              currentUserLocation[1],
              location.latitude,
              location.longitude
            );

            allLocationResults.push({
              text: location.location || locationId,
              type: 'location',
              distance,
              locationData: location,
              matchScore: 1.0,
              category: location.category,
            });
          }
        }
      }

      // Sort by distance and return top 50
      return allLocationResults
        .sort((a, b) => {
          if (a.distance !== undefined && b.distance !== undefined) {
            return a.distance - b.distance;
          }
          return 0;
        })
        .slice(0, MAX_AROUND_ME_RESULTS);
    }

    // Allow brand searches even with short queries
    const searchLower = search.toLowerCase().trim();
    const isBrandSearch = BRANDS.some(
      (brand) => searchLower === brand.toLowerCase() || searchLower.includes(brand.toLowerCase())
    );
    if (search.length < 2 && !isBrandSearch) return [];

    // Pass locationMap for optimized lookups
    return enhancedSearchHandler(search, options, allLocations, activeUserLocation, locationMap);
  }, [search, options, allLocations, userLocation, currentUserLocation, locationMap]);

  // Sort results by distance (always)
  const sortedResults = useMemo(() => {
    return [...searchResults].sort((a, b) => {
      if (a.type === 'category') return -1;
      if (b.type === 'category') return 1;
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return (b.matchScore || 0) - (a.matchScore || 0);
    });
  }, [searchResults]);

  // Group results by type
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {
      categories: [],
      locations: [],
      shops: [],
      parking: [],
    };

    sortedResults.forEach((result) => {
      if (result.type === 'category') {
        groups.categories.push(result);
      } else if (result.text.includes(':::')) {
        groups.shops.push(result);
      } else if (
        result.type === 'bicycle-parking' ||
        result.type === 'mobility-parking' ||
        result.type === 'car-share-bay-operator' ||
        result.type === 'park-and-ride'
      ) {
        groups.parking.push(result);
      } else {
        groups.locations.push(result);
      }
    });

    return groups;
  }, [sortedResults]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        // When usePortal is true on desktop, allow closing by clicking outside
        if (usePortal && window.innerWidth > 768) {
          // Desktop: close when clicking outside
          if (onClose) {
            onClose();
          }
          return;
        }

        // When usePortal is true on mobile, don't close on outside click (overlay handles it)
        if (usePortal && window.innerWidth <= 768) {
          return;
        }

        // On desktop, only close if search is empty
        // On mobile, always close when clicking outside
        if (window.innerWidth <= 768 || search.length === 0) {
          setShowDropdown(false);
          setShowCategories(false);
          setIsInputFocused(false);
        }

        // Close mobile fullscreen when clicking outside
        if (isMobileFullscreen) {
          setIsMobileFullscreen(false);
          document.body.style.overflow = '';
          if (overlayStateNotifiedRef.current) {
            overlayStateNotifiedRef.current = false;
            onOverlayStateChangeRef.current?.(false);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileFullscreen, onOverlayStateChange, search.length, usePortal, onClose]);

  // Handle escape key to close search (mobile fullscreen or desktop portal)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // On mobile, close fullscreen
        if (isMobileFullscreen) {
          closeMobileFullscreen();
        }
        // On desktop with usePortal, close via onClose callback
        else if (usePortal && window.innerWidth > 768) {
          if (onClose) {
            onClose();
          }
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileFullscreen, usePortal, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const clearSearch = () => {
    setSearch('');
    setShowDropdown(false);
    setViewMode('recent'); // Reset to recent searches view
    setShowCategories(false);

    // On mobile, if clearing search and no categories, close fullscreen
    if (isMobileFullscreen && window.innerWidth <= 768) {
      // Keep fullscreen open if input is still focused
      if (document.activeElement?.tagName !== 'INPUT') {
        closeMobileFullscreen();
      }
    }
  };

  const showLocationError = (message: string) => {
    // Create and show error notification similar to map view
    const notification = document.createElement('div');
    notification.className = 'location-error-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideUpNotification 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  };

  const handleCurrentLocation = async () => {
    // Trigger overlay when location button is clicked
    if (window.innerWidth <= 768) {
      setIsMobileFullscreen(true);
      document.body.style.overflow = 'hidden';
      if (!overlayStateNotifiedRef.current) {
        overlayStateNotifiedRef.current = true;
        onOverlayStateChangeRef.current?.(true);
      }
      setIsInputFocused(true);
    } else {
      setIsInputFocused(true);
    }

    if (!navigator.geolocation) {
      showLocationError('Geolocation is not supported by your browser. Please search for a location instead.');
      return;
    }

    setIsLocating(true);
    try {
      // Check permissions first
      if (navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          if (permission.state === 'denied') {
            setIsLocating(false);
            showLocationError(
              'Location access is denied. Please enable location permissions in your browser settings to use this feature.'
            );
            return;
          }
        } catch (permError) {
          // Permissions API might not be supported, continue anyway
          console.log('Permissions API not available, continuing...');
        }
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: [number, number] = [latitude, longitude];
          setIsLocating(false);
          // Set current user location and show nearby locations without populating search
          setCurrentUserLocation(location);
          setSearch(''); // Keep search bar empty
          // Smooth transition: if in around-me view, results will show automatically via searchResults
          // Don't show dropdown - the around-me view handles display
          if (viewMode !== 'around-me') {
            setShowDropdown(true);
          } else {
            setShowDropdown(false); // Let around-me view handle display
          }
          setShowCategories(false);
          console.log('📍 Current location:', latitude, longitude);
        },
        (error) => {
          setIsLocating(false);
          let errorMessage = 'Unable to get your current location.';
          if (error.code === error.PERMISSION_DENIED) {
            errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = 'Location information is unavailable. Please check your device settings.';
          } else if (error.code === error.TIMEOUT) {
            errorMessage = 'Location request timed out. Please try again.';
          }
          showLocationError(errorMessage);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (error) {
      setIsLocating(false);
      console.error('Error checking geolocation permission:', error);
      showLocationError('Unable to access location. Please search for a location instead.');
    }
  };

  const handleSelect = (result: SearchResult) => {
    if (!result.text || typeof result.text !== 'string') {
      console.warn('Invalid result passed to handleSelect:', result);
      return;
    }

    // Save to recent searches
    saveToRecentSearches(result.text);

    if (result.type === 'location') {
      onSelect(result.text);
    } else if (result.type === 'category') {
      const category = result.category as LocationCategory;
      onCategoryFilter(category);
      setSearch(category);
    } else if (result.type === 'park-and-ride') {
      onParkAndRideSelect(result.text);
      setSearch(result.text);
    } else if (result.type === 'bicycle-parking') {
      onBicycleParkingSelect(result.text);
      setSearch(result.text);
    } else if (result.type === 'mobility-parking') {
      onMobilityParkingSelect(result.text);
      setSearch(result.text);
    } else if (result.type === 'car-share-bay-operator') {
      onCarShareBayOperatorSelect(result.text);
      setSearch(result.text);
    }
    setShowDropdown(false);
    setShowCategories(false);

    // Close mobile fullscreen when result is selected
    if (isMobileFullscreen) {
      setIsMobileFullscreen(false);
      setIsInputFocused(false);
      document.body.style.overflow = '';
      if (overlayStateNotifiedRef.current) {
        overlayStateNotifiedRef.current = false;
        onOverlayStateChangeRef.current?.(false);
      }
    }
  };

  const handleRecentSearchClick = (searchTerm: string) => {
    setSearch(searchTerm);
    setShowCategories(false);
    setShowDropdown(true);
    setViewMode('recent');
  };

  const clearCategoryFilter = () => {
    onCategoryFilter(null);
    setSearch('');
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    // On mobile, activate fullscreen overlay mode when input is focused
    if (window.innerWidth <= 768) {
      setIsMobileFullscreen(true);
      // Prevent body scroll when in fullscreen mode
      document.body.style.overflow = 'hidden';
      // Notify parent that overlay is active (only if not already notified via usePortal)
      if (!usePortal && !overlayStateNotifiedRef.current) {
        overlayStateNotifiedRef.current = true;
        onOverlayStateChangeRef.current?.(true);
      }
      // Preserve current view mode - don't reset it when reactivating keyboard
      if (search.length === 0) {
        // Don't change viewMode - preserve the current selection (categories, brands, etc.)
        // Only update showCategories based on current viewMode
        if (viewMode === 'brands') {
          setShowCategories(true);
        } else {
          setShowCategories(false);
        }
        setShowDropdown(false);
      } else {
        setShowCategories(false);
        setShowDropdown(true);
      }
    } else {
      // Desktop behavior
      if (usePortal) {
        // When usePortal is true, always show content (recent searches, brands, etc.) even when search is empty
        setIsInputFocused(true);
        // Don't set showDropdown - let the content views handle display
        // Don't notify here - already handled in useEffect when usePortal changes
      } else {
        // Normal desktop behavior - only show if search has content
        if (search.length > 0) {
          setShowCategories(false);
          setShowDropdown(true);
        }
      }
    }
  };

  // Reset auto-focus flag when usePortal changes and notify parent (only once)
  useEffect(() => {
    if (usePortal) {
      hasAutoFocusedRef.current = false;
      // Reset notification flag to allow notification
      overlayStateNotifiedRef.current = false;
      // Set input as focused to show content immediately
      setIsInputFocused(true);
      // On mobile, also set mobile fullscreen for consistent behavior (keyboard dismissal, etc.)
      if (window.innerWidth <= 768) {
        setIsMobileFullscreen(true);
        document.body.style.overflow = 'hidden';
      }
      // Immediately notify parent that overlay is active when portal is enabled
      // Call the prop directly first (most reliable), then update ref
      if (!overlayStateNotifiedRef.current) {
        overlayStateNotifiedRef.current = true;
        // Call prop directly for immediate notification
        if (onOverlayStateChange) {
          onOverlayStateChange(true);
        }
        // Also update ref for consistency
        onOverlayStateChangeRef.current?.(true);
      }
    } else {
      // When portal is disabled, notify parent that overlay is inactive
      if (overlayStateNotifiedRef.current) {
        overlayStateNotifiedRef.current = false;
        if (onOverlayStateChange) {
          onOverlayStateChange(false);
        }
        onOverlayStateChangeRef.current?.(false);
      }
    }
  }, [usePortal, onOverlayStateChange]);

  // Notify parent when search is active (for desktop when not using portal)
  // When usePortal is true, the usePortal useEffect handles notification
  useEffect(() => {
    // Only handle notifications when NOT using portal (normal search bar usage)
    if (!usePortal && onOverlayStateChangeRef.current) {
      const isSearchActive = isInputFocused || isMobileFullscreen || showDropdown;

      if (isSearchActive) {
        // Search is active - notify parent
        if (!overlayStateNotifiedRef.current) {
          overlayStateNotifiedRef.current = true;
          onOverlayStateChangeRef.current(true);
        }
      } else {
        // Search is not active - notify parent only if we previously notified
        if (overlayStateNotifiedRef.current) {
          overlayStateNotifiedRef.current = false;
          onOverlayStateChangeRef.current(false);
        }
      }
    }
  }, [isInputFocused, isMobileFullscreen, showDropdown, usePortal]);

  // Handle scroll on mobile to dismiss keyboard immediately
  // Works consistently across all view modes (Recent, Brands, Categories, Around Me, etc.)
  // and when triggered from LocationComparer2 (usePortal)
  const handleScroll = useCallback(() => {
    // On mobile, or when usePortal is true (triggered from LocationComparer2)
    const isMobile = window.innerWidth <= 768;
    if ((isMobile && (isMobileFullscreen || usePortal)) || usePortal) {
      // Immediately blur input to dismiss keyboard when scrolling starts
      const input = inputRef.current || (document.querySelector('.search-input') as HTMLInputElement);
      if (input && document.activeElement === input) {
        input.blur();
      }
    }
  }, [isMobileFullscreen, usePortal]);

  const handleInputBlur = () => {
    // Delay to allow click events to fire first
    setTimeout(() => {
      // When usePortal is true, keep input focused to maintain overlay state
      if (usePortal) {
        // Refocus input to keep overlay active
        const input = inputRef.current;
        if (input && document.contains(input)) {
          input.focus();
        }
        return;
      }

      // On desktop, keep input focused if dropdown should be visible (has search query)
      // On mobile, always blur when input loses focus
      if (window.innerWidth > 768 && search.length > 0 && showDropdown) {
        // Keep input focused on desktop when there's a search query
        return;
      }
      setIsInputFocused(false);
    }, 200);
  };

  const closeMobileFullscreen = () => {
    setIsMobileFullscreen(false);
    setIsInputFocused(false);
    setShowDropdown(false);
    setShowCategories(false);
    setSearch('');
    // Restore body scroll
    document.body.style.overflow = '';
    // Notify parent that overlay is inactive
    if (overlayStateNotifiedRef.current) {
      overlayStateNotifiedRef.current = false;
      onOverlayStateChangeRef.current?.(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    if (value.length === 0) {
      setShowCategories(true);
      setShowDropdown(false);
    } else {
      setShowCategories(false);
      setShowDropdown(true);
    }
  };

  const renderResultItem = (result: SearchResult, index: number) => {
    const { text, type, distance, category, locationData } = result;

    // Brand result (category type)
    if (type === 'category') {
      const brandName = category as Brand;
      const IconComponent = BRANDS.includes(brandName) ? BRAND_ICONS[brandName] : null;
      if (!IconComponent) return null;

      return (
        <button
          key={`brand-${index}`}
          onClick={() => {
            setSearch(brandName);
            setShowDropdown(true);
          }}
          className="result-item category-result-item"
        >
          <IconComponent size={18} className="result-icon" />
          <span className="result-text">{highlightText(text, search)}</span>
          <span className="result-badge category-badge">Brand</span>
        </button>
      );
    }

    // Bicycle parking
    if (type === 'bicycle-parking') {
      const cleanOption = text
        .replace('🚲 ', '')
        .replace(' (Bike Park)', '')
        .replace(/ - \d+ spots?$/, '');
      const spotCountMatch = text.match(/ - (\d+) spots?$/);
      const spotCount = spotCountMatch ? spotCountMatch[1] : null;

      return (
        <button key={`bike-${index}`} onClick={() => handleSelect(result)} className="result-item bicycle-parking-item">
          <div className="bicycle-parking-option">
            <span className="bike-icon">🚲</span>
            <div className="bicycle-parking-details">
              <span className="street-name">{highlightText(cleanOption, search)}</span>
              <div className="bicycle-parking-meta">
                <span className="bicycle-parking-label">Bicycle Parking</span>
                {spotCount && <span className="spot-count">{spotCount} spots</span>}
                {distance !== undefined && (
                  <span className="result-distance">
                    <Navigation size={10} />
                    {formatDistance(distance)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      );
    }

    // Mobility parking
    if (type === 'mobility-parking') {
      const cleanOption = text
        .replace('🦽 ', '')
        .replace(' (Mobility Park)', '')
        .replace(/ - \d+ spots?$/, '');
      const spotCountMatch = text.match(/ - (\d+) spots?$/);
      const spotCount = spotCountMatch ? spotCountMatch[1] : null;

      return (
        <button
          key={`mobility-${index}`}
          onClick={() => handleSelect(result)}
          className="result-item mobility-parking-item"
        >
          <div className="mobility-parking-option">
            <span className="wheelchair-icon">🦽</span>
            <div className="mobility-parking-details">
              <span className="street-name">{highlightText(cleanOption, search)}</span>
              <div className="mobility-parking-meta">
                <span className="mobility-parking-label">Mobility Parking</span>
                {spotCount && <span className="spot-count">{spotCount} spots</span>}
                {distance !== undefined && (
                  <span className="result-distance">
                    <Navigation size={10} />
                    {formatDistance(distance)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      );
    }

    // Car share bay operator
    if (type === 'car-share-bay-operator') {
      const cleanOption = text.replace('🚗 ', '').replace(' (Car Share Bay)', '');
      const parts = cleanOption.split(' - ');
      const streetSuburb = parts[0];
      const bayCount = parts[1] ? parts[1].replace(' bays', '') : null;
      const operator = parts[2] || null;

      return (
        <button
          key={`carshare-${index}`}
          onClick={() => handleSelect(result)}
          className="result-item car-share-bay-operator-item"
        >
          <div className="car-share-bay-operator-option">
            <span className="car-icon">🚗</span>
            <div className="car-share-bay-operator-details">
              <span className="street-name">{highlightText(streetSuburb, search)}</span>
              <div className="car-share-bay-operator-meta">
                <span className="car-share-bay-operator-label">Car Share Bay</span>
                {bayCount && <span className="spot-count">{bayCount} bays</span>}
                {operator && <span className="operator">{operator}</span>}
                {distance !== undefined && (
                  <span className="result-distance">
                    <Navigation size={10} />
                    {formatDistance(distance)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      );
    }

    // Regular location or shop
    const isShop = text.includes(':::');
    let displayText = isShop ? text.split(':::')[1].trim() : text;
    const locationName = isShop ? text.split(':::')[0].trim() : text;

    // For ALDI, ZARA, and Subway shops, append location name: "ALDI - [Location Name]", "ZARA - [Location Name]", or "Subway - [Location Name]"
    if (isShop) {
      const shopNameLower = displayText.toLowerCase();
      if (shopNameLower === 'aldi') {
        displayText = `ALDI - ${locationName}`;
      } else if (shopNameLower === 'zara') {
        displayText = `ZARA - ${locationName}`;
      } else if (shopNameLower === 'subway') {
        displayText = `Subway - ${locationName}`;
      }
    }
    // Check if category is a brand, otherwise use null
    const CategoryIcon =
      category && typeof category === 'string' && BRANDS.includes(category as Brand)
        ? BRAND_ICONS[category as Brand] || null
        : null;

    return (
      <button key={`location-${index}`} onClick={() => handleSelect(result)} className="result-item">
        <div className="result-content">
          <div className="result-main">
            <MapPin size={14} className="result-icon" />
            <span className="result-text">{highlightText(displayText, search)}</span>
          </div>
          <div className="result-meta">
            {category && CategoryIcon && (
              <span className="result-badge category-badge-small">
                <CategoryIcon size={10} />
                {category}
              </span>
            )}
            {distance !== undefined && (
              <span className="result-distance">
                <Navigation size={10} />
                {formatDistance(distance)}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  // Handle close callback
  const handleClose = () => {
    closeMobileFullscreen();
    if (onClose) {
      onClose();
    }
  };

  // Update closeMobileFullscreen to call onClose
  const closeMobileFullscreenWithCallback = () => {
    closeMobileFullscreen();
    if (onClose) {
      onClose();
    }
  };

  // Core search component content
  const searchContent = (
    <>
      {/* Mobile Fullscreen Overlay - Always show when in fullscreen mode */}
      {isMobileFullscreen && (
        <div className="mobile-search-overlay" onClick={closeMobileFullscreenWithCallback} aria-label="Close search" />
      )}

      <div
        className={`search-container ${isMobileFullscreen ? 'mobile-fullscreen' : ''} ${containerClassName}`.trim()}
        ref={searchRef}
      >
        <div className="search-input-container">
          <button
            onClick={isMobileFullscreen ? closeMobileFullscreenWithCallback : undefined}
            className={`search-icon ${isMobileFullscreen ? 'back-button' : 'vc-logo'}`}
            aria-label={isMobileFullscreen ? 'Close search' : undefined}
            style={isMobileFullscreen ? { cursor: 'pointer' } : {}}
          >
            {isMobileFullscreen ? <ChevronLeft size={20} /> : 'VC'}
          </button>
          <input
            ref={(el) => {
              inputRef.current = el;
              // Auto-focus when rendered via portal (only once)
              if (usePortal && el && !hasAutoFocusedRef.current) {
                hasAutoFocusedRef.current = true;
                // Use requestAnimationFrame to ensure DOM is ready
                requestAnimationFrame(() => {
                  if (el) {
                    el.focus();
                    // On mobile, trigger click to activate keyboard
                    if (window.innerWidth <= 768) {
                      setTimeout(() => {
                        if (el) {
                          el.click();
                          el.focus();
                        }
                      }, 10);
                    }
                  }
                });
              }
            }}
            type="text"
            value={search}
            placeholder="Search shops, categories, or brands..."
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className="search-input"
            autoFocus={usePortal}
          />
          {selectedCategory && (
            <button onClick={clearCategoryFilter} className="category-chip">
              {selectedCategory}
              <X size={14} />
            </button>
          )}
          {search.length > 0 && (
            <button
              onClick={clearSearch}
              className="clear-search-button"
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Content Area - Scrollable below sticky header */}
        {(isMobileFullscreen || isInputFocused || usePortal) && (
          <div
            className="categories-dropdown search-content-scrollable"
            ref={scrollableRef}
            onScroll={handleScroll}
            onTouchMove={handleScroll}
          >
            {/* Sticky Toggle Header - Always visible when overlay is active */}
            <div className="search-view-toggle-container">
              <div className="search-view-toggle">
                <button
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking toggle button
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMode('recent');
                    setShowCategories(false);
                    // On desktop, keep dropdown visible if there's a search query
                    // On mobile, hide dropdown to show recent searches view
                    if (window.innerWidth <= 768 || search.length === 0) {
                      setShowDropdown(false);
                    } else {
                      // On desktop with search, ensure dropdown stays visible
                      setShowDropdown(true);
                      setIsInputFocused(true);
                      // Refocus input to prevent blur
                      const input = document.querySelector('.search-input') as HTMLInputElement;
                      if (input) {
                        setTimeout(() => input.focus(), 0);
                      }
                    }
                  }}
                  className={`toggle-button ${viewMode === 'recent' ? 'active' : ''}`}
                >
                  <Clock size={16} />
                  <span>Recent</span>
                </button>
                <button
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking toggle button
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMode('brands');
                    setShowCategories(true);
                    // On desktop, keep dropdown visible if there's a search query
                    // On mobile, hide dropdown to show brand filters view
                    if (window.innerWidth <= 768 || search.length === 0) {
                      setShowDropdown(false);
                    } else {
                      // On desktop with search, ensure dropdown stays visible
                      setShowDropdown(true);
                      setIsInputFocused(true);
                      // Refocus input to prevent blur
                      const input = document.querySelector('.search-input') as HTMLInputElement;
                      if (input) {
                        setTimeout(() => input.focus(), 0);
                      }
                    }
                  }}
                  className={`toggle-button ${viewMode === 'brands' ? 'active' : ''}`}
                >
                  <ShoppingBag size={16} />
                  <span>Brands</span>
                </button>
                <button
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking toggle button
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewMode('categories');
                    setShowCategories(false);
                    // On desktop, keep dropdown visible if there's a search query
                    // On mobile, hide dropdown to show categories view
                    if (window.innerWidth <= 768 || search.length === 0) {
                      setShowDropdown(false);
                    } else {
                      // On desktop with search, ensure dropdown stays visible
                      setShowDropdown(true);
                      setIsInputFocused(true);
                      // Refocus input to prevent blur
                      const input = document.querySelector('.search-input') as HTMLInputElement;
                      if (input) {
                        setTimeout(() => input.focus(), 0);
                      }
                    }
                  }}
                  className={`toggle-button ${viewMode === 'categories' ? 'active' : ''}`}
                >
                  <List size={16} />
                  <span>Categories</span>
                </button>
                <button
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking toggle button
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Smooth transition: set view mode first
                    setViewMode('around-me');
                    setShowCategories(false);
                    // On desktop, keep dropdown visible if there's a search query
                    // On mobile, hide dropdown to show around-me view
                    if (window.innerWidth <= 768 || search.length === 0) {
                      setShowDropdown(false);
                    } else {
                      // On desktop with search, ensure dropdown stays visible
                      setShowDropdown(true);
                      setIsInputFocused(true);
                      // Refocus input to prevent blur
                      const input = document.querySelector('.search-input') as HTMLInputElement;
                      if (input) {
                        setTimeout(() => input.focus(), 0);
                      }
                    }

                    // If we already have location, results will show immediately
                    if (currentUserLocation) {
                      // Results will appear instantly via searchResults memo
                    } else {
                      // Otherwise, get location
                      handleCurrentLocation();
                    }
                  }}
                  className={`toggle-button ${viewMode === 'around-me' ? 'active' : ''}`}
                  disabled={isLocating}
                >
                  {isLocating ? <div className="location-spinner-small"></div> : <Navigation size={16} />}
                  <span>Around Me</span>
                </button>
              </div>
            </div>

            {/* Content Views - Show when search is empty, on mobile fullscreen, or on desktop when input is focused (for category views) */}
            {(search.length === 0 || isMobileFullscreen || (window.innerWidth > 768 && isInputFocused && (viewMode === 'categories' || viewMode === 'supermarket' || viewMode === 'electronics' || viewMode === 'beaches'))) && (
              <>
                {/* Recent Searches View */}
                {viewMode === 'recent' && (
                  <div className="recent-searches-view">
                    <div className="recent-searches-header">
                      <Clock size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Recent Searches</span>
                    </div>
                    {recentSearches.length > 0 ? (
                      <div className="recent-searches-list">
                        {recentSearches.map((recentSearch, index) => (
                          <button
                            key={index}
                            onClick={() => handleRecentSearchClick(recentSearch.replace(' ::: ', ' '))}
                            className="recent-search-item"
                          >
                            <Clock size={16} className="recent-search-icon" />
                            <span className="recent-search-text">{recentSearch}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-recent-searches">
                        <p>No recent searches</p>
                        <p className="empty-hint">Start searching to see your history here</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Around Me View - Shows nearby locations */}
                {viewMode === 'around-me' && (
                  <div className="around-me-view">
                    <div className="recent-searches-header">
                      <Navigation size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Locations Near You</span>
                    </div>
                    {isLocating ? (
                      <div className="empty-recent-searches">
                        <div
                          className="location-spinner-small"
                          style={{ margin: '2rem auto', width: '24px', height: '24px' }}
                        ></div>
                        <p>Getting your location...</p>
                      </div>
                    ) : currentUserLocation && searchResults.length > 0 ? (
                      <div className="recent-searches-list">
                        {sortedResults
                          .filter((result) => result.type === 'location' && result.distance !== undefined)
                          .map((result, index) => renderResultItem(result, index))}
                      </div>
                    ) : currentUserLocation ? (
                      <div className="empty-recent-searches">
                        <p>No locations found nearby</p>
                        <p className="empty-hint">Make sure location permissions are enabled</p>
                      </div>
                    ) : (
                      <div className="empty-recent-searches">
                        <p>Click "Around Me" to find locations near you</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Categories View */}
                {viewMode === 'categories' && (
                  <div className="categories-view">
                    <div className="recent-searches-header">
                      <List size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Categories</span>
                    </div>

                    {/* Category Cards - Same grid layout as brands */}
                    <div className="categories-grid">
                      <button
                        onMouseDown={(e) => {
                          // Prevent input blur when clicking category button
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setViewMode('supermarket');
                          setShowCategories(false);
                          // On desktop, keep dropdown visible to show results
                          // On mobile, hide dropdown (fullscreen overlay handles visibility)
                          if (window.innerWidth <= 768) {
                            setShowDropdown(false);
                          } else {
                            setShowDropdown(true);
                            setIsInputFocused(true);
                            // Refocus input to prevent blur
                            const input = document.querySelector('.search-input') as HTMLInputElement;
                            if (input) {
                              setTimeout(() => input.focus(), 0);
                            }
                          }
                        }}
                        className="category-item"
                      >
                        <Store size={20} className="category-icon" />
                        <span className="category-name">Supermarket</span>
                      </button>
                      <button
                        onMouseDown={(e) => {
                          // Prevent input blur when clicking category button
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setViewMode('electronics');
                          setShowCategories(false);
                          // On desktop, keep dropdown visible to show results
                          // On mobile, hide dropdown (fullscreen overlay handles visibility)
                          if (window.innerWidth <= 768) {
                            setShowDropdown(false);
                          } else {
                            setShowDropdown(true);
                            setIsInputFocused(true);
                            // Refocus input to prevent blur
                            const input = document.querySelector('.search-input') as HTMLInputElement;
                            if (input) {
                              setTimeout(() => input.focus(), 0);
                            }
                          }
                        }}
                        className="category-item"
                      >
                        <Music size={20} className="category-icon" />
                        <span className="category-name">Electronics</span>
                      </button>
                      <button
                        onMouseDown={(e) => {
                          // Prevent input blur when clicking category button
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setViewMode('beaches');
                          setShowCategories(false);
                          // On desktop, keep dropdown visible to show results
                          // On mobile, hide dropdown (fullscreen overlay handles visibility)
                          if (window.innerWidth <= 768) {
                            setShowDropdown(false);
                          } else {
                            setShowDropdown(true);
                            setIsInputFocused(true);
                            // Refocus input to prevent blur
                            const input = document.querySelector('.search-input') as HTMLInputElement;
                            if (input) {
                              setTimeout(() => input.focus(), 0);
                            }
                          }
                        }}
                        className="category-item"
                      >
                        <Waves size={20} className="category-icon" />
                        <span className="category-name">Beaches</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Supermarket View */}
                {viewMode === 'supermarket' && (
                  <div className="supermarket-view">
                    <div className="recent-searches-header">
                      <Store size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Supermarkets</span>
                    </div>
                    {getSupermarketResults.length > 0 ? (
                      <div className="recent-searches-list">
                        {getSupermarketResults.map((result, index) => renderResultItem(result, index))}
                      </div>
                    ) : (
                      <div className="empty-recent-searches">
                        <p>No supermarkets found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Electronics View */}
                {viewMode === 'electronics' && (
                  <div className="electronics-view">
                    <div className="recent-searches-header">
                      <Music size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Electronics</span>
                    </div>
                    {getElectronicsResults.length > 0 ? (
                      <div className="recent-searches-list">
                        {getElectronicsResults.map((result, index) => renderResultItem(result, index))}
                      </div>
                    ) : (
                      <div className="empty-recent-searches">
                        <p>No electronics stores found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Beaches View */}
                {viewMode === 'beaches' && (
                  <div className="beaches-view">
                    <div className="recent-searches-header">
                      <Waves size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Beaches</span>
                    </div>
                    {getBeachesResults.length > 0 ? (
                      <div className="recent-searches-list">
                        {getBeachesResults.map((result, index) => renderResultItem(result, index))}
                      </div>
                    ) : (
                      <div className="empty-recent-searches">
                        <p>No beaches found</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Brand Filters View - Only show when search is empty or on mobile */}
                {viewMode === 'brands' && showCategories && (search.length === 0 || isMobileFullscreen) && (
                  <div className="brand-filters-view">
                    <div className="recent-searches-header">
                      <ShoppingBag size={16} className="recent-searches-header-icon" />
                      <span className="recent-searches-header-text">Browse Brands</span>
                    </div>
                    <div className="categories-grid">
                      {BRANDS.map((brand) => {
                        const IconComponent = BRAND_ICONS[brand];
                        return (
                          <button
                            key={brand}
                            onClick={() => {
                              setSearch(brand);
                              setShowCategories(false);
                              setShowDropdown(true);
                              saveToRecentSearches(brand);
                            }}
                            className="category-item"
                          >
                            <IconComponent size={20} className="category-icon" />
                            <span className="category-name">{brand}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Enhanced Search Results Dropdown */}
        {/* Show dropdown if: has results AND (showDropdown is true OR on desktop with search query and input focused) */}
        {sortedResults.length > 0 &&
          (showDropdown || (window.innerWidth > 768 && search.length > 0 && isInputFocused)) && (
            <div
              className="search-dropdown enhanced-search-dropdown"
              ref={resultsRef}
              onScroll={handleScroll}
              onTouchMove={handleScroll}
            >
              {/* Results count */}
              {userLocation && (
                <div className="search-results-header">
                  <span className="results-count">
                    {sortedResults.length} result{sortedResults.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* Categories section */}
              {groupedResults.categories.length > 0 && (
                <div className="results-section" key="categories">
                  <div className="section-header">
                    <Filter size={16} />
                    <span>Categories</span>
                  </div>
                  {groupedResults.categories.map((result, idx) => renderResultItem(result, idx))}
                </div>
              )}

              {/* Locations section */}
              {groupedResults.locations.length > 0 && (
                <div className="results-section" key="locations">
                  <div className="section-header">
                    <MapPin size={16} />
                    <span>Locations</span>
                  </div>
                  {groupedResults.locations.map((result, idx) => renderResultItem(result, idx))}
                </div>
              )}

              {/* Shops section */}
              {groupedResults.shops.length > 0 && (
                <div className="results-section" key="shops">
                  <div className="section-header">
                    <Store size={16} />
                    <span>Shops</span>
                  </div>
                  {groupedResults.shops.map((result, idx) => renderResultItem(result, idx))}
                </div>
              )}

              {/* Parking section */}
              {groupedResults.parking.length > 0 && (
                <div className="results-section" key="parking">
                  <div className="section-header">
                    <Car size={16} />
                    <span>Parking</span>
                  </div>
                  {groupedResults.parking.map((result, idx) => renderResultItem(result, idx))}
                </div>
              )}
            </div>
          )}
      </div>
    </>
  );

  // If usePortal is true, render in a portal with consistent positioning
  if (usePortal) {
    return createPortal(<div className="search-section search-section-portal">{searchContent}</div>, document.body);
  }

  // Otherwise, render normally
  return searchContent;
};

export default SearchDropdown;
